import moment from "moment";
import { bool, number, shape, string } from "prop-types";
import { maskedAccountNumber } from "util/accounts";
import * as configUtils from "util/config";
import * as i18n from "util/i18n";
import { CURRENCY_CODE, CURRENCY_CODE_COELSA, currencyMap } from "../constants";

export const accountDescription = (productAlias, numberV, currency, productType) => {
    if (numberV && numberV !== undefined) {
        if (productAlias) {
            return productAlias.concat(" ", productType, " ", i18n.get(`currency.label.${currency}`));
        }
        return maskedAccountNumber(numberV).concat(" ", productType, " ", i18n.get(`currency.label.${currency}`));
    }
    return "";
};
export const accountUnMaskedDescription = (checksRequestData) =>
    checksRequestData?.account.number.concat(
        " ",
        checksRequestData?.account.productType,
        " ",
        i18n.get(`currency.label.${checksRequestData?.account?.currency}`),
    );

export const productTypes = { CA: "savings", CC: "checking" };

const hasPendienteFirmaTransactionId = (echeq) => {
    const v = echeq?.id_transaccion_firma_pendiente;
    return typeof v === "string" && v.trim() !== "";
};

/** flag + id de transacción PENDING (firma) desde API. */
export const shouldShowPendingEcheqSignaturesHint = (echeq) =>
    configUtils.getBoolean("frontend.show.pendingECHEQs.functionalities", false) &&
    hasPendienteFirmaTransactionId(echeq);

/**
 * Scope del snack “firmas pendientes / esquema” (HU). En comprobantes e-CHEQ bajo shell v3, en cada
 * `TransactionTicket` usar `embeddedV3LayoutScopes={["echeqs", "echeqsPending"]}` para alinear layout y toasts.
 */
export const PENDING_ECHEQ_SIGNATURES_NOTIFICATION_SCOPES = ["echeqsPending"];

/**
 * Solo acciones con hint pendiente de firmas
 * cesión, descuento/negociación, devolución, endoso, certificado de rechazo).
 */
const ECHEQ_PENDING_SIGNATURES_UI = {
    "EMIT-CESION": "cesion.echeq.activity",
    NEGOTIATE: "negotiate.echeq.activity",
    "ACCEPT-RETURN": "accept.return.echeqs",
    ENDORSE: "endorse.echeq.activity",
    "REQUEST-CERTIFICATE": "echeq.request-certificate.action",
};

/**
 * Ruta al comprobante solo si la transacción PENDING corresponde a esta acción; si no, null (flujo normal).
 * Sin {@code id_activity_firma_pendiente} no se asocia a ninguna acción UI (no bloquear el resto).
 * @param {string} uiAction p. ej. "EMIT-CESION" o "NEGOTIATE"
 */
export const getPendingEcheqSignatureTransactionLocation = (echeq, uiAction) => {
    if (!shouldShowPendingEcheqSignaturesHint(echeq) || !uiAction) {
        return null;
    }
    const idActivity = ECHEQ_PENDING_SIGNATURES_UI[uiAction];
    if (!idActivity) {
        return null;
    }
    const apiActivity = (echeq.id_activity_firma_pendiente || "").trim();
    if (!apiActivity || idActivity !== apiActivity) {
        return null;
    }
    return { pathname: `/transaction/${echeq.id_transaccion_firma_pendiente}` };
};

/** Fila: siempre al detalle COELSA. Comprobante solo vía acciones (HU 12350 / menú). */
export const resolveEcheqRowLink = (_echeq, detailPath) => detailPath;

export const checkDisableMultiple = (echeq, environmentCuit, status) => {
    if (
        status === "Activo-pendiente" ||
        status === "Emitido-Pendiente" ||
        status === "Activo" ||
        status.includes(",") ||
        status === "Cesion-pendiente" ||
        status === "Custodia"
    ) {
        if (echeq.estado === "EMITIDO-PENDIENTE") {
            return false;
        }
        if (echeq.estado === "ACTIVO") {
            if (echeq.tenencia_beneficiario_documento === environmentCuit) {
                return false;
            }
            return true;
        }

        if (echeq.estado === "ACTIVO-PENDIENTE") {
            if (echeq.total_endosos === 0 || echeq.tenencia_beneficiario_documento !== environmentCuit) {
                return true;
            }
            return false;
        }
        if (echeq.estado === "CESION-PENDIENTE") {
            if (echeq.total_cesiones > 0 && echeq.cesiones[0].cesionario_documento === environmentCuit) {
                return false;
            }
            return true;
        }
        if (echeq.estado === "CUSTODIA") {
            const fechaPago = moment(echeq.fecha_pago)
                .utc()
                .format("YYYY-MM-DD");
            const fechaHoy = moment()
                .utc()
                .format("YYYY-MM-DD");
            if (echeq.tenencia_beneficiario_documento === environmentCuit && fechaPago > fechaHoy) {
                return false;
            }
        }
    }

    return true;
};

export const echeqConceptLabel = (concept, concatCharacter) => {
    if (concept === "") {
        return "";
    }
    const conceptText = i18n.get(`transfers.concept.${concept}`);

    if (conceptText.includes("transfers.concept")) {
        return concatCharacter.concat(concatCharacter !== "" ? " " : "", concept);
    }
    return concatCharacter.concat(concatCharacter !== "" ? " " : "", conceptText);
};

export const statusTagColor = (state) => {
    switch (state) {
        case "ACTIVO":
        case "CUSTODIA":
        case "NEGOCIACION":
        case "PAGADO":
        case "DEPOSITADO":
        case "PRESENTADO":
            return "#84efd0";
        case "EMITIDO-PENDIENTE":
        case "ACTIVO-PENDIENTE":
        case "DEVOLUCION-PENDIENTE":
        case "NEGOCIACION-PENDIENTE":
        case "CESION-PENDIENTE":
            return "#FFE5B6";
        case "REPUDIADO":
        case "RECHAZADO":
        case "ANULADO":
        case "CADUCADO":
            return "#feb0af";
        default:
            return "#84efd0";
    }
};

export const statusTagColorBatch = (state) => {
    switch (state) {
        case "PENDING":
        case "PROCESSING":
            return "#ffe5b6";
        case "PROCESS_ERROR":
            return "#e6e6e6";
        case "PROCESS_SUCCESS":
        case "SUCCESS":
            return "#84efd0";
        case "PROCESS_REJECTED":
        case "CANCELED":
        case "CANCELLED":
        case "FAILED":
        case "ERROR":
            return "#feb0af";
        default:
            return "#e6e6e6";
    }
};

/**
 * Chip cabecera detalle lote masivo: si la transacción está cancelada pero el lote COELSA sigue en PENDING,
 * mostrar texto/color alineados al comprobante/historial; si no, estado de lote.
 *
 * @param {string} [batchStatus] estado del lote (API detalle batch)
 * @param {string} [idTransactionStatus] {@code summary.idTransactionStatus}
 * @returns {{ message: string, tagBackground: string }}
 */
export const getMassiveBatchDetailHeaderInfoTag = (batchStatus, idTransactionStatus) => {
    const raw = idTransactionStatus == null ? "" : String(idTransactionStatus).trim();
    const tx = raw.toUpperCase();
    if (tx === "CANCELLED" || tx === "CANCELED") {
        const ticketKey = "forms.transaction.ticket.status.CANCELLED";
        let message = i18n.get(ticketKey);
        if (!message || message === ticketKey) {
            message = i18n.get("transaction.status.CANCELLED");
        }
        return {
            message: message || "",
            tagBackground: statusTagColorBatch("FAILED"),
        };
    }
    const b = batchStatus != null ? String(batchStatus) : "";
    return {
        message: i18n.get(`echeq.batch.status.${b.toLowerCase()}`),
        tagBackground: statusTagColorBatch(b),
    };
};

export const dynamicSort = (property, direction) => {
    let sortOrder = 1;
    let attribute = property;
    if (property[0] === "-") {
        sortOrder = -1;
        attribute = property.substr(1);
    }
    let result = 0;

    return (a, b) => {
        /* next line works with strings and numbers,
         * and you may want to customize it to your needs
         */

        const aux1 = a[attribute].trim();
        const aux2 = b[attribute].trim();

        if (direction === "ascendent") {
            if (aux1 === "") {
                result = -1;
            } else if (aux2 === "") {
                result = 1;
            } else {
                result = aux1 < aux2 ? -1 : 1;
            }
        } else if (aux1 === "") {
            result = 1;
        } else if (aux2 === "") {
            result = -1;
        } else {
            result = aux1 > aux2 ? -1 : 1;
        }
        return result * sortOrder;
    };
};

export const receivedEcheqsStatusActions = {
    "EMITIDO-PENDIENTE": ["ADMIT", "REPUDIATE"],
};
export const emittedEcheqsStatusActions = {
    "EMITIDO-PENDIENTE": ["ANULATE-ECHEQ"],
};

export const echeqShape = shape({
    numero_chequera: string.isRequired,
    cheque_numero: string.isRequired,
    beneficiario_nombre: string.isRequired,
    beneficiario_documento_tipo: string.isRequired,
    beneficiario_documento: string.isRequired,
    monto: number.isRequired,
    fecha_pago: string.isRequired,
    emisor_cuit: string.isRequired,
    emisor_razon_social: string.isRequired,
    emisor_moneda: string.isRequired,
    fecha_emision: string.isRequired,
    extra_info: string.isRequired,
    estado: string.isRequired,
    cheque_tipo: string.isRequired,
    cheque_modo: number.isRequired,
    cheque_caracter: string.isRequired,
    cheque_concepto: string.isRequired,
    cheque_motivo_pago: string.isRequired,
    cheque_id: string.isRequired,
    tenencia_beneficiario_nombre: string.isRequired,
    tenencia_beneficiario_documento_tipo: string.isRequired,
    tenencia_beneficiario_documento: string.isRequired,
    total_endosos: number.isRequired,
    total_cesiones: number.isRequired,
    endosos: shape({}).isRequired,
    cesiones: shape({}).isRequired,
    cesion_pendiente: bool.isRequired,
    certificado_emitido: bool.isRequired,
    re_presentar: bool.isRequired,
    cheque_acordado: bool.isRequired,
    solicitando_acuerdo: bool.isRequired,
    cuit_solic_devol: string.isRequired,
    id_transaccion_firma_pendiente: string,
    id_activity_firma_pendiente: string,
});
export function findEmisor(echeq, environmentCuit) {
    let emisor = {
        emisorRazonSocial: echeq.emisor_razon_social,
        emisorDocumento: "CUIT ".concat(" ", echeq.emisor_cuit),
    };

    if (echeq.total_endosos > 0 && echeq.cheque_caracter === "A la orden") {
        const miEndoso = echeq.endosos.find(
            (endoso) => endoso.benef_documento === environmentCuit && endoso.emisor_documento !== environmentCuit,
        );

        if (miEndoso?.emisor_documento) {
            emisor = {
                emisorDocumento: miEndoso.emisor_documento_tipo.concat(" ", miEndoso.emisor_documento),
                emisorRazonSocial: miEndoso.emisor_razon_social,
            };
        }
    }
    if (echeq.total_cesiones > 0 && echeq.cheque_caracter.toLowerCase() === "no a la orden") {
        const miCesion = echeq.cesiones.find(
            (cesion) => cesion.cesionario_documento === environmentCuit && cesion.cedente_documento !== environmentCuit,
        );

        if (miCesion?.cedente_documento) {
            emisor = {
                emisorDocumento: miCesion.cedente_documento_tipo.concat(" ", miCesion.cedente_documento),
                emisorRazonSocial: miCesion.cedente_nombre,
            };
        }
    }
    return emisor;
}
export function findBeneficiary(echeq, environmentCuit) {
    let beneficiario = {
        beneficiarioRazonSocial: echeq.beneficiario_nombre,
        beneficiarioDocumento: echeq.beneficiario_documento_tipo
            .toUpperCase()
            .concat(" ", echeq.beneficiario_documento),
    };

    if (echeq.total_endosos > 0 && echeq.cheque_caracter === "A la orden") {
        const miEndoso = echeq.endosos.find((endoso) => endoso.emisor_documento === environmentCuit);

        if (miEndoso?.emisor_documento) {
            beneficiario = {
                beneficiarioDocumento: miEndoso.benef_documento_tipo.concat(" ", miEndoso.benef_documento),
                beneficiarioRazonSocial: miEndoso.benef_razon_social,
            };
        }
    }
    if (echeq.total_cesiones > 0 && echeq.cheque_caracter.toLowerCase() === "no a la orden") {
        const miCesion = echeq.cesiones.find((cesion) => cesion.cedente_documento === environmentCuit);

        if (miCesion?.cedente_documento) {
            beneficiario = {
                beneficiarioDocumento: miCesion.cesionario_documento_tipo.concat(" ", miCesion.cesionario_documento),
                beneficiarioRazonSocial: miCesion.cesionario_nombre,
            };
        }
    }
    return beneficiario;
}

export const checksDetailStatusTagColor = (state) => {
    switch (state.toUpperCase()) {
        case "ACREDITADO":
        case "PAGADO":
            return "#84efd0";
        case "RECHAZADO":
            return "#feb0af";
        default:
            return "#c7c7c8";
    }
};

export const checksDetailGetCodeMsg = (code) => {
    switch (code.toUpperCase()) {
        case "EN CUSTODIA DEL BANCO":
            return "EN CUSTODIA";
        case "CHEQUES ELECTRONICOS":
            return "ELECTRÓNICO";
        case "CHEQUES":
            return "FÍSICO";
        default:
            return code;
    }
};

export const getChecksDetailAccountName = (checkDetail) => {
    const isEmitted = checkDetail?.isEmittedSelected;

    const accountCheck = isEmitted ? checkDetail.codigoCuentaDebitoCheque : checkDetail.codigoCuentaAcreditacionCheque;

    const systemCheck = isEmitted ? checkDetail.codigoSistemaDebitoCheque : checkDetail.codigoSistemaAcreditacionCheque;

    const currencyCheck = isEmitted ? checkDetail.codigoMonedaDebitoCheque : checkDetail.codigoMonedaAcreditacionCheque;

    return `${maskedAccountNumber(accountCheck)} ${i18n.get(`checksDetail.productType.${systemCheck}`)} ${i18n.get(
        `currency.label.${currencyCheck}`,
    )}`;
};

export const statuses = [
    { value: "1", label: "EN CUSTODIA" },
    { value: "2", label: "RECHAZADO" },
    { value: "3", label: "AL COBRO" },
    { value: "4", label: "ACREDITADO" },
];

export const discountedChecksDetailGetCodeMsg = (code) => {
    switch (code) {
        case 1:
            return "EN CUSTODIA";
        case 2:
            return "RECHAZADO";
        case 3:
            return "AL COBRO";
        default:
            return "ACREDITADO";
    }
};

export const multipleEcheqsTicketData = (echeqAction) => {
    const body = {
        negotiate: [
            { accountLabel: "multiple.echeq.account" },
            { type: "multiple.echeq.type" },
            { reference: "multiple.echeq.reference" },
            { referencevalue: "multiple.echeq.reference.value" },
        ],
        endorse: [
            { type: "multiple.echeq.type" },
            { reference: "multiple.echeq.reference" },
            { referencevalue: "multiple.echeq.reference.value" },
        ],
        cesion: [
            { address: "multiple.echeq.address" },
            { reference: "multiple.echeq.reference" },
            { referencevalue: "multiple.echeq.reference.value" },
        ],
        custody: [{ accountLabel: "multiple.echeq.account" }],
        deposit: [{ accountLabel: "multiple.echeq.account" }],
    };

    return body[echeqAction];
};
export const createEmisionEcheqList = (form) => {
    let echeqList = [];
    const { amount, reference, referencevalue, date, type, quantity, currency } = form;

    for (let i = 0; i < form.quantity; i++) {
        echeqList = [
            ...echeqList,
            { amount: parseFloat((amount / quantity).toFixed(2)), reference, referencevalue, date, type, currency },
        ];
    }

    return echeqList;
};

export const setTenenciaBeneficiario = (echeqAction) => {
    if (!echeqAction.echeqList) {
        return echeqAction;
    }

    const echeqListObject = [];
    echeqAction.echeqList.forEach((echeq) => {
        const object = { ...echeq };
        if (
            object.total_endosos > 0 &&
            (!object.tenencia_beneficiario_documento_tipo || !object.tenencia_beneficiario_documento)
        ) {
            const lastEndorsement = object.endosos[0];
            object.tenencia_beneficiario_nombre = lastEndorsement.benef_razon_social;
            object.tenencia_beneficiario_documento_tipo = lastEndorsement.benef_documento_tipo;
            object.tenencia_beneficiario_documento = lastEndorsement.benef_documento;
        }
        echeqListObject.push(object);
    });

    return { ...echeqAction, echeqList: echeqListObject };
};

export const cleanEcheqObject = (echeqAction, environmentCuit) => {
    let echeqListObject = [];
    let quantity = 0;
    if (!echeqAction.echeqList) {
        const copy = { ...echeqAction };
        delete copy.confirmGoBackPath;
        return copy;
    }
    // I´m cleaning some keys from the json because of the length in extended_data
    echeqAction.echeqList.forEach((echeqJson) => {
        const object = { ...echeqJson, from: findEmisor(echeqJson, environmentCuit) };
        delete object.firmantes;
        delete object.avalistas;
        delete object.endosos;
        delete object.rechazos;
        delete object.cesiones;
        echeqListObject = [...echeqListObject, object];
        quantity += object.monto;
    });
    let currency = "0";
    if (echeqAction.echeqList?.length > 0 && echeqAction.echeqList[0].moneda) {
        currency = currencyMap[echeqAction.echeqList[0].moneda];
    }
    const result = { ...echeqAction, echeqList: echeqListObject, amount: { currency, quantity } };
    delete result.confirmGoBackPath;
    return result;
};

export const getTotalAmountEcheqs = (echeqsToEmit) => echeqsToEmit.reduce((a, b) => a + parseFloat(b.amount, 10), 0);

/**
 * Resolves an echeq display currency from emit payloads or Coelsa echeq objects.
 */
export const getEcheqCurrency = (echeq) =>
    echeq?.currency || echeq?.moneda || echeq?.emisor_moneda || CURRENCY_CODE.ARS;

export const validateEcheqAccounts = (accounts, emit, selectedCurrency) => {
    const filter1 = accounts?.filter(
        (account) => account.currency === (selectedCurrency ? currencyMap[selectedCurrency] : CURRENCY_CODE.ARS),
    );
    const val1 = !filter1?.length;
    const val2 = filter1?.filter((account) => account.productType === "CC" && account?.permiteChequera === "SI");
    return val1 || (emit && !val2.length);
};

export const getAccountMessage = (selectedCurrency, isRetailEnvironment) => {
    const currencyKey = selectedCurrency === CURRENCY_CODE_COELSA.ARS ? "PESOS" : "USD";
    const environmentKey = isRetailEnvironment ? "retail" : "corporate";
    const messageKeyMap = {
        retail: {
            PESOS: "frontend.Echeqs.RequestCA_CC.Pesos.Retail.Text",
            USD: "frontend.Echeqs.RequestCA_CC.USD.Retail.Text",
        },
        corporate: {
            PESOS: "frontend.Echeqs.RequestCuentaCorrientePESOS.Text",
            USD: "frontend.Echeqs.RequestCuentaCorrienteUSD.Text",
        },
    };
    const message = messageKeyMap[environmentKey][currencyKey];
    return message;
};
